import React, { useRef, useState } from "react";
import "./WallSelector.css";
import { useConfiguration } from "src/contexts/ConfigurationContext";

interface Coordinate {
  x: number;
  y: number;
}

interface WandSelectorProps {
  onWallSelect: (wall: UiWall | null) => void;
  onWallDeselect: (wall: UiWall) => void;
  activeWalls: string[];
  storageWalls: string[];
}

export interface UiWall {
  id: string;
  type: string;
  row: number;
  column: number;
  active: boolean;
  selected: boolean;
  inner: boolean;
  storage: boolean;
  length?: number;
}

const getHorizontalWalls = (
  configurator: any,
  rows: number,
  columns: number,
  selectedWall: UiWall | null,
  activeWalls: string[],
  storageWalls: string[]
) => {
  const horizontalWalls: UiWall[] = [];

  for (let r = 0; r <= rows; r++) {
    for (let c = 0; c < columns; c++) {
      const wallId = `h-${r}-${c}`;
      const start: Coordinate = { x: c, y: r };
      const end: Coordinate = { x: c + 1, y: r };
      const length = configurator?.getWallLength
        ? configurator.getWallLength({ start, end })
        : 0;

      horizontalWalls.push({
        row: r,
        column: c,
        type: "horizontal",
        id: wallId,
        selected: selectedWall?.id === wallId,
        active: activeWalls.includes(wallId),
        inner: !(r === 0 || r === rows),
        storage: storageWalls.includes(wallId),
        length,
      });
    }
  }

  return horizontalWalls;
};

const getVerticalWalls = (
  configurator: any,
  rows: number,
  columns: number,
  selectedWall: UiWall | null,
  activeWalls: string[],
  storageWalls: string[]
) => {
  const verticalWalls: UiWall[] = [];

  for (let r = 0; r < rows; r++) {
    for (let c = 0; c <= columns; c++) {
      const wallId = `v-${r}-${c}`;
      const start: Coordinate = { x: c, y: r };
      const end: Coordinate = { x: c, y: r + 1 };
      const length = configurator?.getWallLength
        ? configurator.getWallLength({ start, end })
        : configurator?.getHouse?.().sideLength?.[r] ?? 0;

      verticalWalls.push({
        row: r,
        column: c,
        type: "vertical",
        id: wallId,
        selected: selectedWall?.id === wallId,
        active: activeWalls.includes(wallId),
        inner: !(c === 0 || c === columns),
        storage: storageWalls.includes(wallId),
        length,
      });
    }
  }

  return verticalWalls;
};

const generateBeams = (rows: number, columns: number) => {
  const beamsArray: { row: number; column: number }[] = [];
  for (let r = 0; r <= rows; r++) {
    for (let c = 0; c <= columns; c++) {
      beamsArray.push({ row: r, column: c });
    }
  }
  return beamsArray;
};

const WallSelector: React.FC<WandSelectorProps> = ({
  onWallSelect,
  onWallDeselect,
  activeWalls,
  storageWalls,
}) => {
  const { configurator } = useConfiguration();
  const containerRef = useRef<HTMLDivElement>(null);
  const rows = configurator.getRows();
  const columns = configurator.getColumns();
  const house = configurator.getHouse?.() ?? { depth: 0, sideCount: 1 };
  const depthScale =
    house && house.depth && house.sideCount
      ? house.depth / house.sideCount / 100
      : 20;

  const [selectedWall, setSelectedWall] = useState<UiWall | null>(null);
  const [hoveredSpace, setHoveredSpace] = useState<{
    type: "horizontal" | "vertical";
    row: number;
    column: number;
  } | null>(null);
  const [hoveredVWall, setHoveredVWall] = useState<string | null>(null);

  const horizontalWalls = getHorizontalWalls(
    configurator,
    rows,
    columns,
    selectedWall,
    activeWalls,
    storageWalls
  );
  const verticalWalls = getVerticalWalls(
    configurator,
    rows,
    columns,
    selectedWall,
    activeWalls,
    storageWalls
  );
  const beams = generateBeams(rows, columns);

  const handleWallClick = (wall: UiWall | null) => {
    let isDeselect = false;

    if (
      !storageWalls.includes(wall?.id ?? "") &&
      wall &&
      wall.id === selectedWall?.id &&
      !selectedWall.selected &&
      wall.selected
    ) {
      isDeselect = true;
      onWallDeselect(wall);
    }

    setSelectedWall((prevSelectedWall) =>
      prevSelectedWall?.id === wall?.id ? null : wall
    );

    if (isDeselect) return;
    if (!storageWalls.includes(wall?.id ?? "")) onWallSelect(wall);
  };

  return (
    <div className="walls-selector-container">
      <div className="wand-selector-wrapper" ref={containerRef}>
        <div className="wand-selector">
          {Array.from({ length: rows * 2 + 1 }, (_, rowIndex) => {
            const rowElements: React.ReactNode[] = [];

            if (rowIndex % 2 === 0) {
              for (let c = 0; c < columns; c++) {
                const beam = beams.find(
                  (b) => b.row === rowIndex / 2 && b.column === c
                );
                const hWall =
                  horizontalWalls.find(
                    (hw) => hw.row === rowIndex / 2 && hw.column === c
                  ) ?? null;
                const hWallLengthValue = hWall?.length ?? 0;
                const hWallLength = hWallLengthValue
                  ? hWallLengthValue / 20
                  : 100;
                const isDisabledWall =
                  hWall && (hWall?.storage || hWallLengthValue < 100);

                rowElements.push(
                  <div
                    key={`beam-${beam?.row}-${beam?.column}`}
                    className="beam"
                    style={{
                      width: 20,
                      height: 20,
                      minWidth: 20,
                      minHeight: 20,
                      flex: "none",
                      boxSizing: "border-box",
                    }}
                  />
                );

                rowElements.push(
                  <div
                    key={hWall?.id}
                    className={`wall h-wall ${
                      hWall?.selected ? "selected" : ""
                    } ${hWall?.active ? "active" : ""} ${
                      hWall?.storage ? "storage" : ""
                    } ${isDisabledWall ? "disabled" : ""}`}
                    style={{
                      width: `${hWallLength}px`,
                      minWidth: `55px`,
                      height: 20,
                      flex: "none",
                      position: "relative",
                      cursor: isDisabledWall ? "not-allowed" : "pointer",
                    }}
                    onClick={() => {
                      if (!isDisabledWall) {
                        handleWallClick(hWall);
                      }
                    }}
                  >
                    {hWall?.row === 0 && (
                      <svg
                        className="wall-dimensions-svg"
                        xmlns="http://www.w3.org/2000/svg"
                        style={{
                          position: "relative",
                          top: "12px",
                          width: `${hWallLength < 55 ? 55 : hWallLength}px`,
                          height: "30px",
                        }}
                      >
                        <defs>
                          <marker
                            id="arrowStart"
                            viewBox="0 0 10 10"
                            refX="9"
                            refY="5"
                            markerUnits="strokeWidth"
                            markerWidth="3"
                            markerHeight="3"
                            orient="auto-start-reverse"
                          >
                            <path d="M 0 0 L 10 5 L 0 10 z" stroke="none" />
                          </marker>
                          <marker
                            id="arrowEnd"
                            viewBox="0 0 10 10"
                            refX="9"
                            refY="5"
                            markerUnits="strokeWidth"
                            markerWidth="3"
                            markerHeight="3"
                            orient="auto-start-reverse"
                          >
                            <path d="M 0 0 L 10 5 L 0 10 z" stroke="none" />
                          </marker>
                        </defs>
                        <line
                          x1="0"
                          y1="10"
                          x2={hWallLength < 55 ? 55 : hWallLength}
                          y2="10"
                          stroke="#000000"
                          strokeWidth="2"
                          markerStart="url(#arrowStart)"
                          markerEnd="url(#arrowEnd)"
                        />
                        <text
                          x="50%"
                          y="22"
                          fill="#000000"
                          fontSize="12px"
                          textAnchor="middle"
                        >
                          {hWallLengthValue
                            ? `${
                                hWallLengthValue % 10 === 0
                                  ? (hWallLengthValue / 10).toFixed(0)
                                  : (hWallLengthValue / 10).toFixed(1)
                              }cm`
                            : ""}
                        </text>
                      </svg>
                    )}
                  </div>
                );
              }

              const beam = beams.find(
                (b) => b.row === rowIndex / 2 && b.column === columns
              );
              rowElements.push(
                <div
                  key={`beam-${beam?.row}-${beam?.column}`}
                  className="beam"
                  style={{
                    width: 20,
                    height: 20,
                    minWidth: 20,
                    minHeight: 20,
                    flex: "none",
                    boxSizing: "border-box",
                  }}
                />
              );
            } else {
              for (let c = 0; c <= columns; c++) {
                const vWall = verticalWalls.find(
                  (vw) => vw.row === (rowIndex - 1) / 2 && vw.column === c
                );

                if (vWall) {
                  const vHeight = vWall.length
                    ? vWall.length / depthScale
                    : 160;

                  rowElements.push(
                    <div
                      key={vWall.id}
                      className={`wall v-wall ${
                        vWall.selected ? "selected" : ""
                      } ${vWall?.active ? "active" : ""} ${
                        vWall.inner ? "inner" : ""
                      } ${vWall?.storage ? "storage" : ""}`}
                      style={{
                        width: 20,
                        minWidth: 20,
                        height: `${vHeight}px`,
                        minHeight: `${vHeight}px`,
                        flex: "none",
                        position: "relative",
                        cursor: "pointer",
                      }}
                      onClick={() => handleWallClick(vWall)}
                      onMouseEnter={() => setHoveredVWall(vWall.id)}
                      onMouseLeave={() => setHoveredVWall(null)}
                    >
                      {vWall?.column === 0 && (
                        <svg
                          className="wall-dimensions-svg"
                          xmlns="http://www.w3.org/2000/svg"
                          style={{
                            position: "absolute",
                            left: "-27px",
                            width: "25px",
                            height: `${vHeight}px`,
                          }}
                        >
                          <defs>
                            <marker
                              id="arrowStartV"
                              viewBox="0 0 10 10"
                              refX="9"
                              refY="5"
                              markerUnits="strokeWidth"
                              markerWidth="3"
                              markerHeight="3"
                              orient="auto-start-reverse"
                            >
                              <path d="M 0 0 L 10 5 L 0 10 z" stroke="none" />
                            </marker>
                            <marker
                              id="arrowEndV"
                              viewBox="0 0 10 10"
                              refX="9"
                              refY="5"
                              markerUnits="strokeWidth"
                              markerWidth="3"
                              markerHeight="3"
                              orient="auto-start-reverse"
                            >
                              <path d="M 0 0 L 10 5 L 0 10 z" stroke="none" />
                            </marker>
                          </defs>
                          <line
                            x1="21"
                            y1="0"
                            x2="21"
                            y2={vHeight}
                            stroke="#000000"
                            strokeWidth="2"
                            markerStart="url(#arrowStartV)"
                            markerEnd="url(#arrowEndV)"
                          />
                          <text
                            x="50%"
                            y={vHeight / 2 + 5}
                            fill="#000000"
                            fontSize="12px"
                            textAnchor="middle"
                            transform={`rotate(-90, 12, ${vHeight / 2})`}
                          >
                            {vWall?.length
                              ? `${
                                  vWall.length % 10 === 0
                                    ? (vWall.length / 10).toFixed(0)
                                    : (vWall.length / 10).toFixed(1)
                                }cm`
                              : ""}
                          </text>
                        </svg>
                      )}
                    </div>
                  );
                }

                const ghostWallId = `v-${(rowIndex - 1) / 2}-${c}`;
                const isEdgeWall = c === 0 || c === columns - 1;
                const isGhostStorage =
                  isEdgeWall && storageWalls.includes(ghostWallId);

                if (c < columns) {
                  const hWallForSpace = horizontalWalls.find(
                    (hw) =>
                      hw.row === Math.floor(rowIndex / 2) && hw.column === c
                  );
                  const width = hWallForSpace?.length
                    ? hWallForSpace.length / 20
                    : 100;

                  const vWallForSpace = verticalWalls.find(
                    (vw) => vw.row === (rowIndex - 1) / 2 && vw.column === c
                  );
                  const vHeight =
                    vWallForSpace?.length && depthScale
                      ? vWallForSpace.length / depthScale
                      : 160;

                  const isHovered =
                    !isGhostStorage &&
                    hoveredSpace &&
                    hoveredSpace.type === "vertical" &&
                    hoveredSpace.row === (rowIndex - 1) / 2 &&
                    hoveredSpace.column === c &&
                    width > 100;

                  rowElements.push(
                    <div
                      key={`space-${(rowIndex - 1) / 2}-${c}`}
                      className={`space${isHovered ? " space-hovered" : ""}`}
                      style={{
                        width: `${width}px`,
                        minWidth: `55px`,
                        height: `${vHeight}px`,
                        minHeight: `${vHeight}px`,
                        position: "relative",
                        display: "inline-block",
                        verticalAlign: "top",
                        flex: "none",
                      }}
                      onMouseEnter={() => {
                        if (!isGhostStorage) {
                          setHoveredSpace({
                            type: "vertical",
                            row: (rowIndex - 1) / 2,
                            column: c,
                          });
                        }
                      }}
                      onMouseLeave={() => setHoveredSpace(null)}
                    ></div>
                  );
                }
              }
            }

            return (
              <div key={`row-${rowIndex}`} className="row">
                {rowElements}
              </div>
            );
          }).filter(Boolean)}
        </div>
      </div>
    </div>
  );
};

export default WallSelector;
